<template>
  <div class="panel panel-default">
    <div class="panel-heading">
      <h5>
        Rows
      </h5>
    </div>

    <LoadingSpinner v-if="loading"/>

    <div v-else class="panel-body">
      <Draggable
          element="div"
          :value="contentRows"
          handle=".row-handle"
          chosen-class="interval--dragging"
          @input="rowsSorted"
      >
        <div class="row tw-mb-3" v-for="contentRow in contentRows">
          <div class="col-xs-2 text-center tw-pt-2">
            <i class="fa fa-arrows-alt row-handle" style="cursor: grab;"></i>
          </div>

          <Draggable
              element="div"
              :value="contentRow.attributes.content_blocks"
              handle=".block-handle"
              chosen-class="interval--dragging"
              @input="blocksSorted(contentRow, $event)"
          >
            <div
                v-for="contentBlock in contentRow.attributes.content_blocks"
                style="cursor: grab;"
                class="block-handle"
                :class="'col-xs-' + (contentBlock.width * 6)"
            >
              <div
                  class="tw-bg-gray-200 tw-flex tw-justify-between"
                  style="padding: 6px 12px"
                  :style="'background-color: ' + (contentRow.attributes.content_color_hex || '#e5e7eb')"
              >
                <div>
                  <h5 class="tw-m-0 tw-text-2xl tw-font-bold">
                    {{ contentBlock.block_driver_name }}
                  </h5>

                  <div>
                    <div v-if="contentBlock.block_driver === 'compact-article-list'">
                      {{ contentBlock.block_driver_parameters.text }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'headline'">
                      {{ contentBlock.block_driver_parameters.content }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'large-article-teaser'">
                      Limit: {{ contentBlock.block_driver_parameters.limit || '(none)' }}, offset:
                      {{ contentBlock.block_driver_parameters.offset || '(none)' }}, max:
                      {{ contentBlock.block_driver_parameters.max || '(none)' }}, context:
                      {{ contentBlock.block_driver_parameters.context ? 'Yes' : 'No' }}, partner type:
                      {{ getIsEditorialDescription(contentBlock) }}
                    </div>

                    <div v-if="isList(contentBlock)">
                      Limit: {{ contentBlock.block_driver_parameters.limit || '(none)' }}, offset:
                      {{ contentBlock.block_driver_parameters.offset || '(none)' }}, max:
                      {{ contentBlock.block_driver_parameters.max || '(none)' }}, context:
                      {{ contentBlock.block_driver_parameters.context ? 'Yes' : 'No' }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'large-headline'">
                      {{ contentBlock.block_driver_parameters.content }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'medium-article-list'">
                      {{ contentBlock.block_driver_parameters.headline }}, partner type:
                      {{ getIsEditorialDescription(contentBlock) }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'medium-article-teaser'">
                      <span v-if="contentBlock.block_driver_parameters.content_article_id">(specific article)</span>
                      <span v-else>
                      Offset: {{ contentBlock.block_driver_parameters.offset || '(none)' }}, context:
                      {{ contentBlock.block_driver_parameters.context ? 'Yes' : 'No' }}
                      </span>, partner type:
                      {{ getIsEditorialDescription(contentBlock) }}
                    </div>

                    <div
                        v-if="contentBlock.block_driver === 'medium-banner' || contentBlock.block_driver === 'megaboard-banner' || contentBlock.block_driver === 'megabox-banner'">
                      Ad Unit ID: {{ contentBlock.block_driver_parameters.ad_unit_id || '(none)' }}, keyword:
                      {{ contentBlock.block_driver_parameters.keyword || '(none)' }}
                    </div>

                    <div
                        v-if="contentBlock.block_driver === 'google-medium-banner' || contentBlock.block_driver === 'google-megaboard-banner' || contentBlock.block_driver === 'google-megabox-banner' || contentBlock.block_driver === 'google-streamer-banner'">
                      Ad Unit Path: {{ contentBlock.block_driver_parameters.ad_unit_path || '(none)' }}, element ID:
                      {{ contentBlock.block_driver_parameters.element_id || '(none)' }}
                    </div>

                    <div
                        v-if="contentBlock.block_driver === 'medium-adsense-banner'">
                      Ad Client: {{ contentBlock.block_driver_parameters.ad_client || '(none)' }}, Ad Slot:
                      {{ contentBlock.block_driver_parameters.ad_slot || '(none)' }}
                    </div>

                    <div
                        v-if="contentBlock.block_driver === 'adsense-megaboard-banner'">
                      Ad Client: {{ contentBlock.block_driver_parameters.ad_client || '(none)' }}, Ad Slot:
                      {{ contentBlock.block_driver_parameters.ad_slot || '(none)' }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'node-ticker'">
                      Limit: {{ contentBlock.block_driver_parameters.limit || '(none)' }}
                    </div>

                    <div v-if="contentBlock.block_driver === 'small-article-teaser'">
                      <span v-if="contentBlock.block_driver_parameters.content_article_id">(specific article)</span>
                      <span v-else>
                      Offset: {{ contentBlock.block_driver_parameters.offset || '(none)' }}, context:
                      {{ contentBlock.block_driver_parameters.context ? 'Yes' : 'No' }}
                      </span>, partner type:
                      {{ getIsEditorialDescription(contentBlock) }}
                    </div>
                  </div>
                </div>

                <div class="btn-group list-actions">
                  <button
                      type="button"
                      class="btn btn-sm"
                      @click.prevent="$emit('edit-content-block', {contentRow, contentBlock})"
                  >
                    <i class="fa fa-search"></i>
                  </button>

                  <button
                      type="button"
                      class="btn btn-sm"
                      @click.prevent="deleteContentBlock(contentRow, contentBlock)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </Draggable>

          <div class="col-xs-4" :class="getContentRowFunctionsOffset(contentRow)">
            <div class="btn-group tw-w-full">
              <button type="button" class="btn btn-white btn-block dropdown-toggle" data-toggle="dropdown"
                      style="padding: 4px 12px">
                <i class="fa fa-cogs"></i> Functions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li :class="{'disabled': getOccupiedWidth(contentRow) === 3}">
                  <a @click.prevent="getOccupiedWidth(contentRow) !== 3 && $emit('create-content-block', contentRow)">
                    <i class="fa fa-plus"></i> Create Content Block
                  </a>
                </li>

                <li>
                  <a @click.prevent="editContentRow(contentRow)">
                    <i class="fa fa-edit"></i> Edit Content Row
                  </a>
                </li>

                <li>
                  <a @click.prevent="destroyContentRow(contentRow)">
                    <i class="fa fa-trash"></i> Delete Content Row
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Draggable>

      <button class="btn btn-white btn-block" @click="createContentRow">
        <i class="fa fa-plus"/> Create Content Row
      </button>
    </div>

    <EditModal
        :portal="portal"
        :content-row="editing"
        @updated="contentRowUpdated"
    />
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import Draggable from 'vuedraggable'
import LoadingSpinner from "../../../Layout/LoadingSpinner.vue";
import EditModal from "../../../../components/Administration/ContentRows/Modals/Edit.vue";

export default {
  components: {EditModal, Draggable, LoadingSpinner},

  props: {
    url: {
      required: true,
      type: String
    },

    portal: {
      required: true,
      type: Object
    }
  },

  data() {
    return {
      contentRows: null,

      selectedContentRow: null,
      selectedContentBlock: null,

      editing: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      http.get(this.url).then(response => {
        this.contentRows = response.data.data

        this.loading = false
      })
    },

    getContentRowFunctionsOffset(contentRow) {
      const occupiedWidth = this.getOccupiedWidth(contentRow)

      if (occupiedWidth === 3) {
        return null
      }

      return 'col-xs-offset-' + ((3 - occupiedWidth) * 6)
    },

    getOccupiedWidth(contentRow) {
      return contentRow.attributes.content_blocks.reduce((width, contentBlock) => width + contentBlock.width, 0)
    },

    createContentRow() {
      http.post(this.url).then(response => {
        notify.success('The content row was saved')

        this.contentRows.push(response.data.data)
      })
    },

    editContentRow(contentRow) {
      this.editing = contentRow

      $('#edit-content-row-modal').modal('show')
    },

    contentRowUpdated(contentRow) {
      this.contentRows.splice(this.contentRows.findIndex(existing => existing.id === contentRow.id), 1, contentRow)
    },

    destroyContentRow(contentRow) {
      http.delete(contentRow.links.self).then(() => {
        notify.success('The content row was deleted')

        this.contentRows.splice(this.contentRows.findIndex(existing => existing.id === contentRow.id), 1)
      })
    },

    deleteContentBlock(contentRow, contentBlock) {
      http.delete('/api/v2/content-blocks/' + contentBlock.id).then(() => {
        notify.success('The content block was deleted')

        contentRow.attributes.content_blocks.splice(
            contentRow.attributes.content_blocks.findIndex(existing => existing.id === contentBlock.id),
            1
        )
      })
    },

    rowsSorted(contentRows) {
      this.contentRows = contentRows

      http.put(this.url + '/priorities', {content_rows: contentRows}).then(() => {
        notify.success('The rows were reordered')
      })
    },

    blocksSorted(contentRow, contentBlocks) {
      contentRow.attributes.content_blocks = contentBlocks

      Promise.all(contentBlocks.map((contentBlock, priority) => {
        return http.put('/api/v2/content-blocks/' + contentBlock.id, {...contentBlock, priority})
      })).then(() => {
        notify.success('The blocks were reordered')
      })
    },

    isList(contentBlock) {
      return [
        'event-list',
        'event-teaser',
        'job-list',
        'job-teaser',
        'partner-list',
        'topic-list',
        'topic-teaser'
      ].includes(contentBlock.block_driver)
    },

    getIsEditorialDescription(contentBlock) {
      if(contentBlock.block_driver_parameters.is_editorial){
        return 'Editorial'
      }

      if(contentBlock.block_driver_parameters.is_editorial === false){
        return 'Partner'
      }

      return 'All'
    }
  }
}
</script>